/**
 * Shared time formatting — Intl.RelativeTimeFormat (single-unit relative),
 * t() templates (compound countdown), Intl.DateTimeFormat (clocks).
 */
import { resolveTranslation } from "@/contexts/LanguageContext";
import type { AppLanguage } from "@/lib/appLanguage";
import { getAppLanguage } from "@/lib/appLanguage";
import { interpolate } from "@/lib/interpolate";
import { calendarDaysFromToday, getRelativeMinutes } from "@/lib/timeMath";
import { getOsloDateStr } from "@/lib/timezone";

export type { AppLanguage };

export function intlLocale(language: AppLanguage): string {
  switch (language) {
    case "no":
      return "nb-NO";
    case "en":
      return "en-GB";
    case "uk":
      return "uk-UA";
    case "ar":
      return "ar-u-nu-latn";
    case "zh":
      return "zh-CN-u-nu-latn";
  }
}

function t(language: AppLanguage, key: string): string {
  return resolveTranslation(language, key);
}

/** Interpolate a translation key with variables. */
export function formatMessage(
  language: AppLanguage,
  key: string,
  vars: Record<string, string | number>,
): string {
  return interpolate(t(language, key), vars);
}

const rtfCache = new Map<string, Intl.RelativeTimeFormat>();

function getRelativeTimeFormat(language: AppLanguage): Intl.RelativeTimeFormat {
  const locale = intlLocale(language);
  let formatter = rtfCache.get(locale);
  if (!formatter) {
    formatter = new Intl.RelativeTimeFormat(locale, {
      numeric: "always",
      style: "short",
    });
    rtfCache.set(locale, formatter);
  }
  return formatter;
}

const dateTimeCache = new Map<string, Intl.DateTimeFormat>();

const OSLO_TZ = "Europe/Oslo";

const osloPartsFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: OSLO_TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  hourCycle: "h23",
  weekday: "short",
});

const WEEKDAY_INDEX: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

function osloParts(date: Date): { key: string; hour: number; weekday: number } {
  const parts = osloPartsFormatter.formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? "";
  return {
    key: `${get("year")}-${get("month")}-${get("day")}`,
    hour: Number(get("hour")) % 24,
    weekday: WEEKDAY_INDEX[get("weekday")] ?? 0,
  };
}

/** Hour of day (0–23) in Norwegian time, regardless of device timezone. */
export function osloHour(date: Date = new Date()): number {
  return osloParts(date).hour;
}

/** Day of week (0 = Sunday) in Norwegian time. */
export function osloWeekday(date: Date = new Date()): number {
  return osloParts(date).weekday;
}

/** YYYY-MM-DD calendar date in Norwegian time. */
export function osloDateKey(date: Date = new Date()): string {
  return osloParts(date).key;
}

function getDateTimeFormatter(
  language: AppLanguage,
  options: Intl.DateTimeFormatOptions,
  cacheKey: string,
): Intl.DateTimeFormat {
  const locale = intlLocale(language);
  const key = `${locale}:${cacheKey}`;
  let formatter = dateTimeCache.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, { timeZone: OSLO_TZ, ...options });
    dateTimeCache.set(key, formatter);
  }
  return formatter;
}

function getClockFormatter(language: AppLanguage): Intl.DateTimeFormat {
  return getDateTimeFormatter(
    language,
    { hour: "2-digit", minute: "2-digit", hour12: false },
    "clock",
  );
}

function getWeekdayShortFormatter(language: AppLanguage): Intl.DateTimeFormat {
  return getDateTimeFormatter(
    language,
    { weekday: "short", timeZone: "UTC" },
    "weekday-short",
  );
}

export type WeekdayIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/** Short weekday name for JS/edge day indexes, where 0 is Sunday. */
export function getWeekdayShort(
  language: AppLanguage,
  day: WeekdayIndex,
): string {
  const sunday = Date.UTC(2025, 0, 5);
  const label = getWeekdayShortFormatter(language)
    .format(new Date(sunday + day * 24 * 60 * 60 * 1000))
    .replace(/\.$/, "");
  return label.charAt(0).toLocaleUpperCase(intlLocale(language)) + label.slice(1);
}

/** Absolute clock time HH:MM for the active locale. */
export function formatClock(
  iso: string,
  language: AppLanguage = getAppLanguage(),
): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "--:--";
  return getClockFormatter(language).format(date);
}

/** Clock HH:MM from a Date instance. */
export function formatClockFromDate(
  date: Date,
  language: AppLanguage = getAppLanguage(),
): string {
  if (Number.isNaN(date.getTime())) return "--:--";
  return getClockFormatter(language).format(date);
}

/** "Sist oppdatert HH:MM" style label. */
export function formatLastUpdatedClock(
  dateOrMs: Date | number,
  language: AppLanguage = getAppLanguage(),
): string {
  const date = dateOrMs instanceof Date ? dateOrMs : new Date(dateOrMs);
  return `${t(language, "last_updated")} ${formatClockFromDate(date, language)}`;
}

/** Duration from seconds — "Xt Ymin", "Y min", or "< 1 min". */
export function formatDurationSeconds(
  seconds: number,
  language: AppLanguage = getAppLanguage(),
): string {
  const minutes = Math.round(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) {
    return interpolate(t(language, "time_duration_hours_minutes"), { h: hours, m: mins });
  }
  if (minutes < 1 && seconds > 0) {
    return t(language, "time_duration_under_minute");
  }
  return interpolate(t(language, "time_duration_minutes"), { n: mins });
}

/** Bare minute count — "5 min" (no om/in prefix). */
export function formatMinutesBare(
  minutes: number,
  language: AppLanguage = getAppLanguage(),
): string {
  return interpolate(t(language, "time_duration_minutes"), { n: minutes });
}

/** Approximate drive time — "~14 min" or "〰 ~14 min". */
/** «ca. X min» — used when drive time is an estimate (no road route). */
export function formatDriveMinutesEstimate(
  minutes: number,
  language: AppLanguage = getAppLanguage(),
): string {
  return interpolate(t(language, "time_drive_estimate"), { n: minutes });
}

export function formatDriveMinutesApprox(
  minutes: number,
  language: AppLanguage = getAppLanguage(),
  isFerryWave = false,
): string {
  const key = isFerryWave ? "time_drive_ferry_approx" : "time_drive_approx";
  return interpolate(t(language, key), { n: minutes });
}

/** Delay badge — "+5 min". */
export function formatDelayPlus(
  minutes: number,
  language: AppLanguage = getAppLanguage(),
): string {
  return interpolate(t(language, "time_delay_plus"), { n: minutes });
}

/** Compact hours+minutes — "2t 42m". */
export function formatCompoundHoursShort(
  totalMinutes: number,
  language: AppLanguage = getAppLanguage(),
): string {
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return interpolate(t(language, "time_compound_hours_short"), { h: hours, m: mins });
}

/** Map travel-time pill — "~45 min" / "~1 t 33 min" / ferry wave variant. */
export function formatTravelTimePill(
  totalMinutes: number,
  language: AppLanguage = getAppLanguage(),
  isFerryRoute = false,
): string {
  const prefix = isFerryRoute ? "ferry_" : "";
  let key: string;
  let vars: Record<string, string | number>;
  if (totalMinutes < 60) {
    key = `time_travel_pill_${prefix}minutes`;
    vars = { n: totalMinutes };
  } else {
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    if (mins === 0) {
      key = `time_travel_pill_${prefix}hours`;
      vars = { h: hours };
    } else {
      key = `time_travel_pill_${prefix}hours_minutes`;
      vars = { h: hours, m: mins };
    }
  }
  return interpolate(t(language, key), vars);
}

/** Browser tab countdown — "🚢 5 min – Original title". */
export function formatTabTitleCountdown(
  minutesLeft: number,
  originalTitle: string,
  language: AppLanguage = getAppLanguage(),
): string {
  return interpolate(t(language, "time_tab_countdown"), {
    n: minutesLeft,
    title: originalTitle,
  });
}

/** Weekday + day + month (long) — header dates. */
export function formatDateLong(
  date: Date,
  language: AppLanguage = getAppLanguage(),
): string {
  if (Number.isNaN(date.getTime())) return "";
  return getDateTimeFormatter(
    language,
    { weekday: "long", day: "numeric", month: "long" },
    "date-long",
  ).format(date);
}

/** Weekday (short) + day + month (short). */
export function formatDateMedium(
  date: Date,
  language: AppLanguage = getAppLanguage(),
): string {
  if (Number.isNaN(date.getTime())) return "";
  return getDateTimeFormatter(
    language,
    { weekday: "short", day: "numeric", month: "short" },
    "date-medium",
  ).format(date);
}

/** Day + month (short) + year + clock — schedule footers. */
export function formatDateTimeMedium(
  date: Date,
  language: AppLanguage = getAppLanguage(),
): string {
  if (Number.isNaN(date.getTime())) return "";
  return getDateTimeFormatter(
    language,
    {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    },
    "date-time-medium",
  ).format(date);
}

/** Day + month (short) + year — price/date footers. */
export function formatDateShort(
  date: Date,
  language: AppLanguage = getAppLanguage(),
): string {
  if (Number.isNaN(date.getTime())) return "";
  return getDateTimeFormatter(
    language,
    { day: "numeric", month: "short", year: "numeric" },
    "date-short",
  ).format(date);
}

/** Trip day chip — I dag / I morgen / localized short date. */
export function formatTripDayLabel(
  isoTime: string | undefined,
  language: AppLanguage = getAppLanguage(),
  now = new Date(),
): string | null {
  if (!isoTime) return null;
  const depDate = new Date(isoTime);
  if (Number.isNaN(depDate.getTime())) return null;
  const depKey = osloDateKey(depDate);
  if (depKey === osloDateKey(now)) return t(language, "time_day_today");
  if (depKey === osloDateKey(new Date(now.getTime() + 24 * 60 * 60 * 1000))) return t(language, "tomorrow");
  return formatDateMedium(depDate, language);
}

/**
 * Past-relative label from elapsed seconds (useLastUpdated, DataTrustStrip).
 * Uses t() templates for product phrasing ("for X sek siden").
 */
export function formatRelativePastSeconds(
  secondsAgo: number,
  language: AppLanguage = getAppLanguage(),
): string {
  if (secondsAgo < 10) {
    return t(language, "time_just_now");
  }
  if (secondsAgo < 60) {
    return interpolate(t(language, "time_past_for_seconds"), { n: secondsAgo });
  }
  const mins = Math.floor(secondsAgo / 60);
  if (mins < 60) {
    return interpolate(t(language, "time_past_for_minutes"), { n: mins });
  }
  const hours = Math.floor(mins / 60);
  return interpolate(t(language, "time_past_hours_minutes"), {
    h: hours,
    m: mins % 60,
  });
}

/** Past-relative cache age ("Akkurat nå", "15 min siden", compound hours). */
export function formatCacheAge(
  cachedAt: number,
  language: AppLanguage = getAppLanguage(),
): string {
  const ageMin = Math.round((Date.now() - cachedAt) / 60000);
  if (ageMin < 1) {
    return t(language, "time_just_now_cap");
  }
  if (ageMin < 60) {
    return interpolate(t(language, "time_past_minutes"), { n: ageMin });
  }
  const hours = Math.floor(ageMin / 60);
  return interpolate(t(language, "time_past_hours_minutes_compact"), {
    h: hours,
    m: ageMin % 60,
  });
}

/**
 * Future countdown from total minutes — single unit or compound (option b templates).
 */
export function formatCountdownMinutes(
  totalMinutes: number,
  language: AppLanguage = getAppLanguage(),
): string {
  if (totalMinutes <= 0) {
    return t(language, "now");
  }
  if (totalMinutes < 60) {
    return interpolate(t(language, "time_in_minutes"), { n: totalMinutes });
  }
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  if (mins === 0) {
    return interpolate(t(language, "time_in_hours"), { h: hours });
  }
  return interpolate(t(language, "time_in_hours_minutes"), { h: hours, m: mins });
}

/**
 * Single-unit future relative via Intl (e.g. en "in 5 min" when templates not required).
 */
export function formatRelativeFutureUnit(
  value: number,
  unit: Intl.RelativeTimeFormatUnit,
  language: AppLanguage = getAppLanguage(),
): string {
  return getRelativeTimeFormat(language).format(value, unit);
}

/**
 * Single-unit past relative via Intl.
 */
export function formatRelativePastUnit(
  value: number,
  unit: Intl.RelativeTimeFormatUnit,
  language: AppLanguage = getAppLanguage(),
): string {
  return getRelativeTimeFormat(language).format(-Math.abs(value), unit);
}

/** Day-aware departure countdown (ferry rows, samband lists). */
export function formatDepartureCountdown(
  iso: string,
  language: AppLanguage = getAppLanguage(),
  now = Date.now(),
): string {
  const relMin = getRelativeMinutes(iso, now);
  if (relMin <= 0) {
    return t(language, "now");
  }
  if (relMin < 60) {
    return interpolate(t(language, "time_in_minutes"), { n: relMin });
  }

  const daysDiff = calendarDaysFromToday(iso, new Date(now));
  if (daysDiff === 1) {
    return t(language, "time_tomorrow");
  }
  if (daysDiff > 1 && daysDiff < 7) {
    return new Intl.DateTimeFormat(language === "no" ? "nb-NO" : language, {
      weekday: "long",
      timeZone: "Europe/Oslo",
    }).format(new Date(iso));
  }
  if (daysDiff >= 7) {
    return interpolate(t(language, "time_in_days"), { n: daysDiff });
  }

  if (relMin >= 180) {
    return "";
  }
  return formatCountdownMinutes(relMin, language);
}

/**
 * «Når»-tekst for avgangsrader i kaikortet. Gir alltid tekst:
 * <60 min «om N min», senere i dag «i dag», i morgen, 2–6 dager ukedag, ellers «om N dager».
 */
export function formatDepartureWhenLabel(
  iso: string,
  language: AppLanguage = getAppLanguage(),
  now = Date.now(),
): string {
  const relMin = getRelativeMinutes(iso, now);
  if (relMin <= 0) return t(language, "now");
  if (relMin < 60) return interpolate(t(language, "time_in_minutes"), { n: relMin });
  // Kalenderdager i Europe/Oslo (ikke enhetens tidssone).
  const daysDiff = Math.round(
    (Date.parse(getOsloDateStr(new Date(iso).getTime())) - Date.parse(getOsloDateStr(now))) / 86_400_000,
  );
  if (daysDiff <= 0) return t(language, "time_today");
  if (daysDiff === 1) return t(language, "time_tomorrow");
  if (daysDiff < 7) {
    return new Intl.DateTimeFormat(language === "no" ? "nb-NO" : language, {
      weekday: "long",
      timeZone: "Europe/Oslo",
    }).format(new Date(iso));
  }
  return interpolate(t(language, "time_in_days"), { n: daysDiff });
}
